import React from 'react';

import { useHasViewPermission } from '../../../hooks';
import useDebounce from '../../../hooks/useDebounce';
import { Event, Tags } from '../../../interfaces';
import { invalidateEventTagsQuery, updateInfiniteEvent } from '../../../queries';
import { TagsService } from '../../../services';
import { deepCopy, deepEqual } from '../../../utils';
import { Permission } from '../../atoms';
import { EventTags } from './EventTags';

interface Props {
  event: Event;
}

/**
 * The event tags wih autosave component is used to display and edit tags for an event. It AUTOMATICALLY
 * saves the tags to the server when its destroyed or when a new event is passed in, if the
 * user is an admin-kind of user.
 *
 * @param props
 * @returns
 */

export function EventTagsWithAutosave(props: Props) {
  const [userTags, setUserTags] = React.useState<Tags>({});
  const [initialTags, setInitialTags] = React.useState<Tags | null>(null);
  const debouncedTags = useDebounce(userTags, 500);
  const hasPermission = useHasViewPermission('events:write:tags');

  const previousTags = React.useRef({});
  const latestDebouncedTags = React.useRef(debouncedTags);

  // Update ref with latest debounced tags
  React.useEffect(() => {
    latestDebouncedTags.current = debouncedTags;
  }, [debouncedTags]);

  React.useEffect(() => {
    if (!hasPermission) {
      return; // Short circuit if the user doesn't have access
    }

    if (initialTags === null) {
      return; // short circuit if tags are not loaded yet
    }

    // Don't save tags if they haven't changed
    const hasTagsChanged = !deepEqual(debouncedTags, previousTags.current);

    const prevTagsToIds = previousTags.current && Object.keys(previousTags.current).map((item) => +item.slice(3));
    const debouncedTagsToIds = debouncedTags && Object.keys(debouncedTags).map((item) => +item.slice(3));

    const findElementsMissingBetweenArrays = (arr1, arr2) => {
      return arr1?.filter((id) => !arr2?.includes(id));
    };

    const tagsToDelete = findElementsMissingBetweenArrays(prevTagsToIds, debouncedTagsToIds);

    const performTagOperations = async () => {
      if (hasTagsChanged) {
        await TagsService.addTagsToEvent(debouncedTags, props.event.id);
      }

      if (tagsToDelete?.length > 0) {
        await TagsService.removeTagsFromEvent(tagsToDelete, props.event.id);
      }

      // After all tag operations are settled, fetch fresh tags and update infinite event cache
      if (hasTagsChanged || tagsToDelete?.length > 0) {
        const freshTags = await TagsService.getTagsForEvent(props.event.id);
        const updatedEvent: Event = { ...props.event, tags: freshTags as unknown as Event['tags'] };
        updateInfiniteEvent(updatedEvent);
      }
    };

    performTagOperations();

    previousTags.current = deepCopy(debouncedTags);
  }, [debouncedTags]);

  React.useEffect(() => {
    invalidateEventTagsQuery(props.event.id, debouncedTags);

    // Cleanup function to run on unmount
    return () => {
      invalidateEventTagsQuery(props.event.id, latestDebouncedTags.current);
    };
  }, [props.event.id]);

  return (
    <Permission permission="events:read:tags">
      <EventTags event={props.event} onUserTagChange={setUserTags} onTagsLoad={setInitialTags} withAutocomplete />
    </Permission>
  );
}
